import { Link } from 'react-router-dom';
import Button from '@/components/base/Button';
import SectionHeading from '@/components/base/SectionHeading';
import CtaBanner from '@/components/feature/CtaBanner';
import Process from '@/pages/home/components/Process';
import FAQAccordion from '@/pages/home/components/FAQAccordion';
import { detailSteps } from '@/mocks/services';

export interface ServiceConfig {
  slug: string;
  name: string;
  valueProp: string;
  heroImage: string;
  included: { title: string; text: string }[];
  scenarios: { icon: string; title: string; text: string }[];
  faqs: { q: string; a: string }[];
}

export default function ServiceDetail({ service }: { service: ServiceConfig }) {
  const quoteHref = `/contact?service=${service.slug}`;
  return (
    <>
      <section className="px-4 md:px-6 lg:px-20 pt-8 md:pt-12 pb-16 md:pb-20">
        <div className="max-w-content mx-auto">
          <nav aria-label="Breadcrumb" className="text-sm text-foreground-600">
            <ol className="flex flex-wrap items-center gap-2">
              <li><Link to="/" className="hover:text-secondary-500 cursor-pointer whitespace-nowrap">Home</Link></li>
              <li aria-hidden="true"><i className="ri-arrow-right-s-line" /></li>
              <li><Link to="/services" className="hover:text-secondary-500 cursor-pointer whitespace-nowrap">Services</Link></li>
              <li aria-hidden="true"><i className="ri-arrow-right-s-line" /></li>
              <li aria-current="page" className="text-foreground-950">{service.name}</li>
            </ol>
          </nav>
          <div className="mt-8 md:mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-6 lg:items-end fade-up">
            <h1 className="lg:col-span-7 font-heading text-[38px] md:text-[56px] lg:text-[64px] leading-[1.06] font-medium tracking-tight">{service.name}</h1>
            <div className="lg:col-span-4 lg:col-start-9">
              <p className="text-base md:text-lg leading-[1.6] text-foreground-600">{service.valueProp}</p>
              <Button href={quoteHref} className="mt-6">Request a Quote <i className="ri-arrow-right-line" /></Button>
            </div>
          </div>
          <div data-reveal="image" className="mt-10 md:mt-14 w-full h-[280px] md:h-[440px] lg:h-[560px] rounded-[24px] overflow-hidden bg-background-200">
            <img loading="eager" fetchPriority="high" src={service.heroImage} alt={`${service.name} by a CLEARY professional`} title={`${service.name} — CLEARY`} className="w-full h-full object-cover object-top" />
          </div>
        </div>
      </section>

      <section className="px-4 md:px-6 lg:px-20 py-16 md:py-20 lg:py-[112px] bg-background-100">
        <div className="max-w-content mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-6">
          <div className="lg:col-span-4">
            <SectionHeading eyebrow="WHAT'S INCLUDED" title="Care Where It Counts." sub="Typical areas covered. Exact tasks are confirmed in your service plan." />
          </div>
          <ul className="lg:col-span-7 lg:col-start-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8">
            {service.included.map((it) => (
              <li key={it.title} className="flex gap-4 py-6 border-t border-background-300">
                <span className="w-7 h-7 rounded-full bg-background-50 text-secondary-500 flex items-center justify-center shrink-0"><i className="ri-check-line" /></span>
                <div>
                  <h3 className="text-base font-medium">{it.title}</h3>
                  <p className="mt-1.5 text-[15px] leading-[1.6] text-foreground-600">{it.text}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="px-4 md:px-6 lg:px-20 py-16 md:py-20 lg:py-[112px]">
        <div className="max-w-content mx-auto">
          <SectionHeading eyebrow="WHEN IT MAKES SENSE" title="Is This the Right Fit?" />
          <div data-reveal-group className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6">
            {service.scenarios.map((sc) => (
              <div key={sc.title} className="rounded-2xl border border-background-300 bg-background-50 p-6 md:p-8">
                <span className="w-11 h-11 rounded-full bg-secondary-100 text-secondary-700 flex items-center justify-center"><i className={`${sc.icon} text-lg`} /></span>
                <h3 className="mt-6 font-heading text-[20px] lg:text-[22px] leading-[1.3] font-medium">{sc.title}</h3>
                <p className="mt-3 text-[15px] leading-[1.6] text-foreground-600">{sc.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Process items={detailSteps} eyebrow="HOW IT WORKS" title="Simple from Start to Finish." />
      <FAQAccordion items={service.faqs} eyebrow="SERVICE FAQ" title="Questions About This Service." />
      <CtaBanner title="Ready for a Fresh Start?" text="Share a few details about your home and we'll follow up to discuss next steps." primary={{ label: 'Get a Quote', href: quoteHref }} secondary={{ label: 'All Services', href: '/services' }} />
    </>
  );
}